<script setup>
import {defineProps, reactive} from "vue";
import Pagination from "@/Models/Pagination";
import {Link} from "@inertiajs/inertia-vue3";
import {Inertia} from "@inertiajs/inertia";
import ButtonShape from "@/Shared/ButtonShape.vue";
import {ChevronDownIcon} from "@heroicons/vue/24/solid";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from "dayjs/plugin/timezone";
import {isEmpty} from "lodash";
import Faq from "@/Models/Faq";
import DeleteModal from "@/Shared/Dashboard/Modals/DeleteModal.vue";

let props = defineProps({
    faqs: Array,
    filters: Object,
    current_url: String,
});

let deleteFaqSettings = reactive({
  deleteFaqModalIsOpen: false,
  faqToBeDelete: null,
})

let currentUrl = window.location.toString();
let filters = reactive(props.filters);
let pagination = reactive(new Pagination(props.faqs));

dayjs.extend(utc);
dayjs.extend(timezone);
function UTCToHumanReadable(u) {
    return dayjs(u).utc().local().tz(dayjs.tz.guess()).format('MMMM DD, YYYY hh:mm A');
}

function deleteFaq() {
  if (isEmpty(deleteFaqSettings.faqToBeDelete)) {
    return;
  }
  let faqObject = new Faq(deleteFaqSettings.faqToBeDelete);
  faqObject.delete()
  deleteFaqSettings.deleteFaqModalIsOpen = false
}

function moveOrder(id,direction) {
  Inertia.post('/backoffice/faqs/' + id + '/move-order', {direction:direction})
}
</script>

<template>
    <DeleteModal :open="deleteFaqSettings.deleteFaqModalIsOpen" modalTitle="Delete Category" :modalDescription="(deleteFaqSettings.faqToBeDelete)?'Are you sure you want to Delete ('+deleteFaqSettings.faqToBeDelete.question+')? All of your data will be permanently removed from our servers forever. This action cannot be undone.':''">
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button type="button" class="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm" @click.prevent="deleteFaq()">Delete</button>
        <button type="button" class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm" @click.prevent="deleteFaqSettings.deleteFaqModalIsOpen = false">Cancel</button>
      </div>
    </DeleteModal>
    <div>
        <div class="max-w-7xl mx-auto px-4 flex justify-between items-center">
            <h1 class="text-2xl font-semibold text-gray-900">Frequently Asked Questions</h1>
            <Link href="/backoffice/faqs/create" class="text-sm text-wgn-blue-4">Add new Faq</Link>
        </div>
        <div class="max-w-7xl mx-auto px-4">
            <div class="py-4">
                <div>
                    <div class="mb-2 overflow-hidden">
                        <div class="rounded-lg bg-white px-4 sm:px-0 lg:px-0">
                            <div class="flex flex-col border border-gray-200 md:rounded-lg">
                                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-300">
                                                <thead class="bg-gray-50">
                                                <tr>
                                                    <th
                                                        scope="col"
                                                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                    >
                                                        <Link
                                                            class="group inline-flex"
                                                            :href="currentUrl"
                                                            :data="{
                                                        sort_by: 'id',
                                                        sort_order: filters.sort_order === 'desc' ? 'asc' : 'desc',
                                                    }"
                                                        >
                                                            ID
                                                            <span
                                                                :class="{
                                                            'invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible':
                                                                filters.sort_by !== 'id',
                                                            'ml-2 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300':
                                                                filters.sort_by === 'id',
                                                            'rotate-180':
                                                                filters.sort_by === 'id' &&
                                                                filters.sort_order === 'asc',
                                                        }"
                                                            >
                                                        <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                        </Link>
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                    >
                                                        <span class="group inline-flex">Question</span>
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                    >
                                                        <Link
                                                            class="group inline-flex"
                                                            :href="currentUrl"
                                                            :data="{
                                                        sort_by: 'order_column',
                                                        sort_order: filters.sort_order === 'desc' ? 'asc' : 'desc',
                                                    }"
                                                        >
                                                            Order
                                                            <span
                                                                :class="{
                                                            'invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible':
                                                                filters.sort_by !== 'order_column',
                                                            'ml-2 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300':
                                                                filters.sort_by === 'order_column',
                                                            'rotate-180':
                                                                filters.sort_by === 'order_column' &&
                                                                filters.sort_order === 'asc',
                                                        }"
                                                            >
                                                        <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                        </Link>
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                    >
                                                        <Link
                                                            class="group inline-flex"
                                                            :href="currentUrl"
                                                            :data="{
                                                        sort_by: 'created_at',
                                                        sort_order: filters.sort_order === 'desc' ? 'asc' : 'desc',
                                                    }"
                                                        >
                                                            Created At
                                                            <span
                                                                :class="{
                                                            'invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible':
                                                                filters.sort_by !== 'created_at',
                                                            'ml-2 flex-none rounded bg-gray-200 text-gray-900 group-hover:bg-gray-300':
                                                                filters.sort_by === 'created_at',
                                                            'rotate-180':
                                                                filters.sort_by === 'created_at' &&
                                                                filters.sort_order === 'asc',
                                                        }"
                                                            >
                                                            <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
                                                        </span>
                                                        </Link>
                                                    </th>
                                                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                                        <span class="sr-only">Edit</span>
                                                        <span class="sr-only">Delete</span>
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-200 bg-white">
                                                <tr v-for="faq in faqs.data" :key="faq.id">
                                                    <td
                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                                                    >
                                                        {{ faq.id }}
                                                    </td>
                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {{ faq.question }}
                                                    </td>
                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize">
                                                        {{ faq.order_column }}
                                                    </td>
                                                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        {{ UTCToHumanReadable(faq.created_at) }}
                                                    </td>
                                                    <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                      <div class="flex flex-row items-center space-x-2">
                                                        <div>
                                                          <div class="flex flex-col space-y-0.5">
                                                            <button class="p-1 border border-gray-300 rounded-full hover:bg-gray-100" @click.prevent="moveOrder(faq.id,'up')">
                                                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                                              </svg>
                                                            </button>
                                                            <button class="p-1 border border-gray-300 rounded-full hover:bg-gray-100" @click.prevent="moveOrder(faq.id,'down')">
                                                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                                                              </svg>
                                                            </button>
                                                          </div>
                                                        </div>
                                                        <Link :href="'/backoffice/faqs/'+faq.id+'/edit'" class="text-indigo-600 hover:text-indigo-900 mr-3">Edit</Link>
                                                        <button @click.prevent="deleteFaqSettings.deleteFaqModalIsOpen = true; deleteFaqSettings.faqToBeDelete = faq" class="text-red-600 hover:text-indigo-900">Delete</button>
                                                      </div>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <nav
                            class="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white bg-white px-4 py-3 sm:px-6"
                            aria-label="Pagination"
                        >
                            <div class="hidden sm:block">
                                <p class="font-inter text-sm text-gray-700">
                                    Showing
                                    {{ ' ' }}
                                    <span class="font-medium">{{ pagination.meta.from }}</span>
                                    {{ ' ' }}
                                    to
                                    {{ ' ' }}
                                    <span class="font-medium">{{ pagination.meta.to }}</span>
                                    {{ ' ' }}
                                    of
                                    {{ ' ' }}
                                    <span class="font-medium">{{ pagination.meta.total }}</span>
                                    {{ ' ' }}
                                    results
                                </p>
                            </div>
                            <div class="flex flex-1 justify-between space-x-4 sm:justify-end">
                                <Link :href="pagination.links.prev">
                                    <ButtonShape v-if="pagination.links.prev" type="gray"> Previous</ButtonShape>
                                </Link>
                                <Link :href="pagination.links.next">
                                    <ButtonShape v-if="pagination.links.next" type="gray"> Next</ButtonShape>
                                </Link>
                            </div>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <!-- List of FAQs with drag-and-drop reordering -->
<!--        <div v-model="faqs" @end="updateOrder" class="space-y-4">-->
<!--            <div v-for="(faq, index) in faqs" :key="faq.id" class="bg-white p-4 rounded-lg shadow-md">-->
<!--                <div class="flex items-center justify-between">-->
<!--                    <div class="text-lg font-medium">{{ faq.question }}</div>-->
<!--                    <div class="flex items-center">-->
<!--                        <button @click.prevent="editFaq(faq)" class="text-indigo-600 hover:text-indigo-900 mr-2">Edit</button>-->
<!--                        <button @click.prevent="deleteFaq(faq.id)" class="text-red-600 hover:text-red-900">Delete</button>-->
<!--                    </div>-->
<!--                </div>-->
<!--                <div class="mt-2">{{ faq.answer }}</div>-->
<!--            </div>-->
<!--        </div>-->

<!--        &lt;!&ndash; Add FAQ button &ndash;&gt;-->
<!--        <div class="mt-4">-->
<!--            <inertia-link :href="'/faqs/create'" class="text-indigo-600 hover:text-indigo-900 font-medium">Add FAQ</inertia-link>-->
<!--        </div>-->
    </div>
</template>

<script>
import BackofficeLayout from '@/Layouts/BackofficeLayout'
export default {
    layout: BackofficeLayout,
}
</script>
